import type { DocumentGroup } from '../services/partnerOnboardingService';
import type { PartnerDocumentReviewItem } from '../services/partnerOnboardingService';
import type { PartnerRow } from '../services/partnerUserService';
import { entityLabel } from '../constants/documentReviewFlow';
import { SERVICE_HUB_OPTIONS } from '../constants/partnerServices';

export function zoneIdFromRef(zoneId?: string | { _id: string; name?: string; code?: string }) {
  if (!zoneId) return '';
  return typeof zoneId === 'object' ? zoneId._id : zoneId;
}

export function zoneName(zoneId?: string | { _id: string; name: string; code?: string }) {
  if (!zoneId) return 'All zones';
  if (typeof zoneId === 'object') return zoneId.name || zoneId.code || '—';
  return zoneId;
}

/** URL-safe code slug for document groups and document types. */
export function slugifyDocumentCode(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

export function groupZoneIds(group: Pick<DocumentGroup, 'zoneId' | 'zoneIds'>): string[] {
  if (group.zoneIds?.length) {
    return group.zoneIds.map((z) => (typeof z === 'object' ? z._id : z));
  }
  const single = zoneIdFromRef(group.zoneId);
  return single ? [single] : [];
}

export function groupServiceHubs(group: Pick<DocumentGroup, 'serviceHub' | 'serviceHubs'>): string[] {
  if (group.serviceHubs?.length) return group.serviceHubs;
  return group.serviceHub ? [group.serviceHub] : [];
}

function zoneDisplayName(z: { name?: string; code?: string } | string): string {
  if (typeof z === 'string') return z;
  if (z.name && z.code) return `${z.name} (${z.code})`;
  return z.name || z.code || '—';
}

export function groupZoneChipItems(
  group: Pick<DocumentGroup, 'zoneId' | 'zoneIds'>,
  allZones?: Array<{ _id: string; name?: string; code?: string }>,
): Array<{ id: string; label: string }> {
  const ids = groupZoneIds(group);
  if (!ids.length) {
    if (allZones?.length) {
      return allZones.map((z) => ({
        id: z._id,
        label: zoneDisplayName(z),
      }));
    }
    return [{ id: '__all__', label: 'All zones' }];
  }
  if (group.zoneIds?.length) {
    return group.zoneIds.map((z) => {
      if (typeof z === 'object') {
        return { id: z._id, label: zoneDisplayName(z) };
      }
      const match = allZones?.find((zone) => zone._id === z);
      return { id: z, label: match ? zoneDisplayName(match) : z };
    });
  }
  const single = group.zoneId;
  if (!single) return [{ id: '__all__', label: 'All zones' }];
  if (typeof single === 'object') {
    return [{ id: single._id, label: zoneDisplayName(single) }];
  }
  const match = allZones?.find((zone) => zone._id === single);
  return [{ id: single, label: match ? zoneDisplayName(match) : single }];
}

export function groupZonesLabel(
  group: Pick<DocumentGroup, 'zoneId' | 'zoneIds'>,
  allZones?: Array<{ _id: string; name?: string; code?: string }>,
) {
  return groupZoneChipItems(group, allZones).map((z) => z.label).join(', ');
}

export function hubLabel(hub: string) {
  return SERVICE_HUB_OPTIONS.find((h) => h.value === hub)?.label || hub;
}

export function groupHubsLabel(group: Pick<DocumentGroup, 'serviceHub' | 'serviceHubs'>) {
  const hubs = groupServiceHubs(group);
  if (!hubs.length) return 'All hubs';
  return hubs.map(hubLabel).join(', ');
}

/** True when the group is explicitly scoped to the hub (excludes "all hubs" groups). */
export function groupMatchesServiceHub(
  group: Pick<DocumentGroup, 'serviceHub' | 'serviceHubs'>,
  hub: string,
) {
  if (!hub) return true;
  const hubs = groupServiceHubs(group);
  return hubs.length > 0 && hubs.includes(hub);
}

/** One-line scope summary for list headers and duplicate differentiation. */
export function groupScopeSummary(
  group: Pick<DocumentGroup, 'entityType' | 'zoneId' | 'zoneIds' | 'serviceHub' | 'serviceHubs'>,
  allZones?: Array<{ _id: string; name?: string; code?: string }>,
) {
  return `${entityLabel(group.entityType)} · ${groupZonesLabel(group, allZones)} · ${groupHubsLabel(group)}`;
}

function scopeListsOverlap(a: string[], b: string[]): boolean {
  if (!a.length || !b.length) return true;
  return a.some((id) => b.includes(id));
}

export function groupsScopeOverlap(
  a: Pick<DocumentGroup, 'zoneId' | 'zoneIds' | 'serviceHub' | 'serviceHubs'>,
  b: Pick<DocumentGroup, 'zoneId' | 'zoneIds' | 'serviceHub' | 'serviceHubs'>,
) {
  return scopeListsOverlap(groupZoneIds(a), groupZoneIds(b))
    && scopeListsOverlap(groupServiceHubs(a), groupServiceHubs(b));
}

export type GroupScopeCandidate = {
  name: string;
  code: string;
  entityType: string;
  zoneIds: string[];
  serviceHubs: string[];
};

export function findConflictingDocumentGroup(
  groups: DocumentGroup[],
  candidate: GroupScopeCandidate,
  excludeId?: string,
): DocumentGroup | undefined {
  const errors = getDocumentGroupFormErrors(groups, candidate, excludeId);
  if (!errors.name && !errors.code) return undefined;
  const nameKey = candidate.name.trim().toLowerCase();
  const codeKey = candidate.code.trim().toLowerCase();
  return groups.find((g) => {
    if (excludeId && g._id === excludeId) return false;
    if (g.entityType !== candidate.entityType) return false;
    if (g.code.trim().toLowerCase() === codeKey) return true;
    if (g.name.trim().toLowerCase() === nameKey && groupsScopeOverlap(g, candidate)) return true;
    return false;
  });
}

export function getDocumentGroupFormErrors(
  groups: DocumentGroup[],
  candidate: GroupScopeCandidate,
  excludeId?: string,
): { name?: string; code?: string } {
  const errors: { name?: string; code?: string } = {};
  const nameKey = candidate.name.trim().toLowerCase();
  const codeKey = candidate.code.trim().toLowerCase();

  if (codeKey) {
    const codeConflict = groups.find((g) => {
      if (excludeId && g._id === excludeId) return false;
      if (g.entityType !== candidate.entityType) return false;
      return g.code.trim().toLowerCase() === codeKey;
    });
    if (codeConflict) {
      errors.code = `Code "${candidate.code.trim()}" is already used for ${entityLabel(candidate.entityType)}. Choose a different code.`;
    }
  }

  if (nameKey) {
    const nameConflict = groups.find((g) => {
      if (excludeId && g._id === excludeId) return false;
      if (g.entityType !== candidate.entityType) return false;
      if (g.name.trim().toLowerCase() !== nameKey) return false;
      return groupsScopeOverlap(g, candidate);
    });
    if (nameConflict) {
      errors.name = `Group name "${candidate.name.trim()}" already exists for ${groupScopeSummary(nameConflict)}. Change the name or adjust zone/service scope.`;
    }
  }

  return errors;
}

export function duplicateDocumentGroupIds(groups: DocumentGroup[]): Set<string> {
  const dupes = new Set<string>();
  for (let i = 0; i < groups.length; i += 1) {
    for (let j = i + 1; j < groups.length; j += 1) {
      const a = groups[i];
      const b = groups[j];
      if (a.entityType !== b.entityType) continue;
      if (a.name.trim().toLowerCase() !== b.name.trim().toLowerCase()) continue;
      if (groupsScopeOverlap(a, b)) {
        dupes.add(a._id);
        dupes.add(b._id);
      }
    }
  }
  return dupes;
}

const ENTITY_SORT_ORDER = ['driver', 'delivery_partner', 'merchant', 'vendor', 'service_provider', 'partner'];

/** Partner Profile → Driver KYC → Vehicle Documents → other roles. */
export function sortDocumentGroupsForAdmin(groups: DocumentGroup[]): DocumentGroup[] {
  return [...groups].sort((a, b) => {
    const aProfile = a.code === 'partner_profile' || a.isSystem ? 0 : 1;
    const bProfile = b.code === 'partner_profile' || b.isSystem ? 0 : 1;
    if (aProfile !== bProfile) return aProfile - bProfile;

    const ae = ENTITY_SORT_ORDER.indexOf(a.entityType);
    const be = ENTITY_SORT_ORDER.indexOf(b.entityType);
    const ar = ae >= 0 ? ae : 99;
    const br = be >= 0 ? be : 99;
    if (ar !== br) return ar - br;

    const aVehicle = /vehicle/i.test(a.code) || /vehicle/i.test(a.name) ? 1 : 0;
    const bVehicle = /vehicle/i.test(b.code) || /vehicle/i.test(b.name) ? 1 : 0;
    if (aVehicle !== bVehicle) return aVehicle - bVehicle;

    return a.name.localeCompare(b.name);
  });
}

export function findConflictingDocumentConfig(
  configs: Array<{ _id: string; name: string; code: string; groupId?: string | { _id: string } }>,
  candidate: { name: string; code: string; groupId: string },
  excludeId?: string,
) {
  const nameKey = candidate.name.trim().toLowerCase();
  const codeKey = candidate.code.trim().toLowerCase();
  return configs.find((d) => {
    if (excludeId && d._id === excludeId) return false;
    if (groupIdFromRef(d.groupId) !== candidate.groupId) return false;
    if (d.code.trim().toLowerCase() === codeKey) return true;
    return d.name.trim().toLowerCase() === nameKey;
  });
}

export function documentCountByGroup(
  configs: Array<{ groupId?: string | { _id: string }; isActive?: boolean }>,
) {
  const map: Record<string, { total: number; active: number }> = {};
  configs.forEach((doc) => {
    const gid = groupIdFromRef(doc.groupId);
    if (!gid) return;
    if (!map[gid]) map[gid] = { total: 0, active: 0 };
    map[gid].total += 1;
    if (doc.isActive !== false) map[gid].active += 1;
  });
  return map;
}

export function documentCountLabel(stats?: { total: number; active: number }) {
  const total = stats?.total ?? 0;
  const active = stats?.active ?? 0;
  if (total === 0) return '0/0';
  return `${active}/${total}`;
}

export function documentConfigIdFromRef(
  documentConfigId?: string | { _id: string; name?: string; code?: string },
) {
  if (!documentConfigId) return '';
  return typeof documentConfigId === 'object' ? documentConfigId._id : documentConfigId;
}

export function groupIdFromRef(groupId?: string | { _id: string; name?: string; code?: string }) {
  if (!groupId) return '';
  return typeof groupId === 'object' ? groupId._id : groupId;
}

export function reviewItemToPartner(row: PartnerDocumentReviewItem): PartnerRow {
  const zoneRef = row.zoneId;
  const zoneId = zoneIdFromRef(zoneRef);
  return {
    _id: row.partnerId,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    phone: row.phone,
    userType: row.entityType,
    isActive: true,
    isVerified: false,
    isBlocked: false,
    partnerStatus: 'pending_approval',
    zoneId: typeof zoneRef === 'object' && zoneRef
      ? zoneRef
      : zoneId
        ? { _id: zoneId, name: zoneName(zoneRef) }
        : undefined,
    serviceHub: row.serviceHub,
    onboarding: row.onboarding
      ? { _id: row.onboarding._id, status: row.onboarding.status }
      : undefined,
    pendingDocCount: row.pendingDocCount,
  };
}

export function partnerDisplayName(row: { firstName: string; lastName?: string }) {
  return `${row.firstName} ${row.lastName || ''}`.trim();
}

export function matchesDocScope(
  row: { entityType: string; zoneId?: string | { _id: string }; serviceHub?: string; groupId?: string | { _id: string } },
  filters: { entityType?: string; zoneId?: string; serviceHub?: string; groupId?: string },
) {
  if (filters.entityType && row.entityType !== filters.entityType) return false;
  if (filters.groupId && groupIdFromRef(row.groupId) !== filters.groupId) return false;
  if (filters.zoneId) {
    const rowZone = zoneIdFromRef(row.zoneId);
    if (rowZone && rowZone !== filters.zoneId) return false;
  }
  if (filters.serviceHub && row.serviceHub && row.serviceHub !== filters.serviceHub) return false;
  return true;
}

export { entityLabel };
